import { useState } from 'react';
import { Plus, Search, Pencil, Trash2, Shield, ShieldCheck, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { UsuarioModal } from '@/components/usuarios/UsuarioModal';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useUsers, UserWithDetails, UserPermissions } from '@/hooks/useUsers';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

const ROLE_BADGES: Record<string, { label: string; className: string }> = {
  admin: { 
    label: 'Admin', 
    className: 'bg-[hsl(var(--accent-100))] text-[hsl(var(--accent-700))] border-[hsl(var(--accent-300))]'
  },
  manager: { 
    label: 'Gerente', 
    className: 'bg-[hsl(var(--basic-100))] text-[hsl(var(--basic-700))] border-[hsl(var(--basic-300))]'
  },
  operator: { 
    label: 'Operador', 
    className: 'bg-muted text-muted-foreground border-border'
  },
};

const Usuarios = () => {
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserWithDetails | null>(null);
  const [deleteUserId, setDeleteUserId] = useState<string | null>(null);
  const [isInviting, setIsInviting] = useState(false);
  
  const { toast } = useToast();
  const { 
    users, 
    isLoading, 
    updatePermissions, 
    updateRole, 
    updateProfile,
    isUpdating,
    refetch
  } = useUsers();

  const filtered = users.filter(u => {
    if (!search.trim()) return true;
    const query = search.toLowerCase();
    return (
      u.full_name?.toLowerCase().includes(query) ||
      u.user_id.toLowerCase().includes(query)
    );
  });

  const handleEdit = (user: UserWithDetails) => {
    setSelectedUser(user);
    setModalOpen(true);
  };

  const handleCreate = () => {
    setSelectedUser(null);
    setModalOpen(true);
  };

  const handleInvite = async (data: {
    email: string;
    fullName: string;
    role: 'admin' | 'manager' | 'operator';
    permissions: Partial<UserPermissions>;
  }) => {
    setIsInviting(true);
    try {
      const { data: result, error } = await supabase.functions.invoke('invite-user', {
        body: {
          email: data.email,
          fullName: data.fullName,
          role: data.role,
          permissions: data.permissions,
        }
      });

      if (error) {
        throw new Error(error.message || 'Erro ao convidar usuário');
      }

      if (result?.error) {
        throw new Error(result.error);
      }

      toast({
        title: 'Convite enviado!',
        description: `Um email foi enviado para ${data.email} com instruções de acesso.`,
      });

      setModalOpen(false);
      refetch();
    } catch (err: any) {
      console.error('Invite error:', err);
      throw err;
    } finally {
      setIsInviting(false);
    }
  };

  const handleSave = (data: {
    profileId: string;
    fullName: string;
    role: 'admin' | 'manager' | 'operator';
    permissions: Partial<UserPermissions>;
  }) => {
    const user = users.find(u => u.id === data.profileId);
    if (!user) return;

    // Update profile name
    updateProfile({ profileId: data.profileId, fullName: data.fullName });
    
    // Update role if changed
    if (user.role !== data.role) {
      updateRole({ userId: user.user_id, role: data.role });
    }
    
    // Update permissions
    updatePermissions({ userId: user.user_id, permissions: data.permissions });
    
    setModalOpen(false);
  };

  const countPermissions = (user: UserWithDetails): number => {
    if (user.role === 'admin') return 10; // All permissions
    if (!user.permissions) return 0;
    
    const perms = user.permissions;
    return [
      perms.criar_cliente, perms.alterar_cliente, perms.apagar_cliente,
      perms.criar_regua, perms.alterar_regua, perms.apagar_regua,
      perms.listar_usuario, perms.alterar_usuario, perms.apagar_usuario,
      perms.alterar_configuracoes
    ].filter(Boolean).length;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Shield className="h-6 w-6 text-[hsl(var(--basic-500))]" />
            Usuários
          </h1>
          <p className="text-muted-foreground">Gerencie usuários e permissões de acesso</p>
        </div>
        <Button 
          onClick={handleCreate} 
          className="gap-2 hover-pop bg-[hsl(var(--accent-500))] hover:bg-[hsl(var(--accent-600))]"
        >
          <Plus className="h-4 w-4" />
          Novo Usuário
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-card rounded-xl border p-4 flex items-center gap-4">
          <div className="p-3 rounded-lg bg-[hsl(var(--accent-100))]">
            <ShieldCheck className="h-5 w-5 text-[hsl(var(--accent-600))]" />
          </div>
          <div>
            <p className="text-2xl font-bold text-foreground">
              {users.filter(u => u.role === 'admin').length}
            </p>
            <p className="text-xs text-muted-foreground">Administradores</p>
          </div>
        </div>
        <div className="bg-card rounded-xl border p-4 flex items-center gap-4">
          <div className="p-3 rounded-lg bg-[hsl(var(--basic-100))]">
            <User className="h-5 w-5 text-[hsl(var(--basic-600))]" />
          </div>
          <div>
            <p className="text-2xl font-bold text-foreground">
              {users.filter(u => u.role === 'manager').length}
            </p>
            <p className="text-xs text-muted-foreground">Gerentes</p>
          </div>
        </div>
        <div className="bg-card rounded-xl border p-4 flex items-center gap-4">
          <div className="p-3 rounded-lg bg-muted">
            <User className="h-5 w-5 text-muted-foreground" />
          </div>
          <div>
            <p className="text-2xl font-bold text-foreground">
              {users.filter(u => u.role === 'operator' || !u.role).length}
            </p>
            <p className="text-xs text-muted-foreground">Operadores</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por nome..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Lista de Usuários */}
      <div className="bg-card rounded-xl border overflow-hidden">
        {/* Header */}
        <div className="grid grid-cols-[2fr_1fr_1fr_8rem] gap-4 p-4 bg-muted/50 border-b font-medium text-sm text-muted-foreground">
          <span>Usuário</span>
          <span>Cargo</span>
          <span>Permissões</span>
          <span className="text-right">Ações</span>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="p-8 text-center text-muted-foreground">
            Carregando...
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            {search ? 'Nenhum usuário encontrado.' : 'Nenhum usuário cadastrado.'}
          </div>
        ) : (
          <div className="divide-y">
            {filtered.map((user) => {
              const roleBadge = ROLE_BADGES[user.role || 'operator'];
              const permCount = countPermissions(user);
              
              return (
                <div 
                  key={user.id} 
                  className="grid grid-cols-[2fr_1fr_1fr_8rem] gap-4 p-4 items-center hover:bg-muted/30 transition-colors"
                >
                  {/* User Info */}
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[hsl(var(--basic-100))] flex items-center justify-center font-bold text-[hsl(var(--basic-700))]">
                      {user.full_name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div>
                      <p className="font-bold text-foreground">
                        {user.full_name || 'Sem nome'}
                      </p>
                      <p className="text-xs text-muted-foreground font-mono">
                        {user.user_id.slice(0, 8)}...
                      </p>
                    </div>
                  </div>
                  
                  {/* Role Badge */}
                  <div>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${roleBadge.className}`}>
                      {roleBadge.label}
                    </span>
                  </div>
                  
                  {/* Permissions Count */}
                  <div>
                    <span className="text-sm text-muted-foreground">
                      {user.role === 'admin' ? (
                        <span className="text-[hsl(var(--accent-600))] font-medium">Acesso total</span>
                      ) : (
                        `${permCount}/10 ativas`
                      )}
                    </span>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex justify-end gap-2">
                    <Button 
                      variant="outline" 
                      size="icon" 
                      className="h-8 w-8"
                      onClick={() => handleEdit(user)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="icon" 
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => setDeleteUserId(user.id)}
                      disabled
                      title="Exclusão de usuários em desenvolvimento"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <UsuarioModal 
        open={modalOpen} 
        onOpenChange={setModalOpen} 
        usuario={selectedUser}
        onSave={handleSave}
        onInvite={handleInvite}
        isSaving={isUpdating || isInviting}
      />

      <ConfirmDialog
        open={!!deleteUserId}
        onOpenChange={(open) => !open && setDeleteUserId(null)}
        title="Excluir Usuário"
        description="Tem certeza que deseja excluir este usuário? Esta ação não pode ser desfeita."
        onConfirm={() => {
          // TODO: Implement user deletion
          setDeleteUserId(null);
        }}
        confirmText="Excluir"
        variant="destructive"
      />
    </div>
  );
};

export default Usuarios;
